import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, ChevronLeft, Activity, MapPin, Phone, Calendar, AlertTriangle,
  MessageSquare, Camera, Stethoscope, Users, FileText, CalendarPlus,
  CheckCircle2, X, Clock, Pill, Trash2, Clock3, History, Bell,
  TrendingUp, TrendingDown, Minus
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WOUND_LABELS = {
  ulcera_venosa: "Úlcera Venosa",
  pe_diabetico: "Pé Diabético",
  lesao_por_pressao: "Lesão por Pressão"
};
const RISK_LABELS = { critico: "Crítico", alto: "Alto", medio: "Médio", baixo: "Baixo" };
const STATUS_LABELS = { ativo: "Ativo", cicatrizado: "Cicatrizado", encaminhado: "Encaminhado", amputado: "Amputado" };
const APPT_TYPE_LABELS = {
  retorno: "Retorno", curativo: "Curativo", avaliacao: "Avaliação", interconsulta: "Interconsulta"
};
const APPT_STATUS_COLORS = {
  agendado: "bg-blue-100 text-blue-800",
  realizado: "bg-green-100 text-green-800",
  cancelado: "bg-gray-100 text-gray-500",
};

function WoundCard({ wound }) {
  return (
    <Link
      to={`/wounds/${wound.wound_id}`}
      className="block bg-white rounded-xl border border-border p-4 hover-lift transition-all"
      data-testid={`wound-card-${wound.wound_id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
            {WOUND_LABELS[wound.wound_type] || wound.wound_type}
          </p>
          {wound.location && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" />{wound.location}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full risk-${wound.risk_level}`}>
            {RISK_LABELS[wound.risk_level] || "Baixo"}
          </span>
          <span className="text-xs text-muted-foreground">{STATUS_LABELS[wound.status]}</span>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
        <span>{wound.start_date ? `Início: ${wound.start_date}` : ""}</span>
        <span>{wound.photos_count || 0} foto(s)</span>
      </div>
      {wound.latest_evolution && (
        <div className="mt-2 pt-2 border-t border-border/50">
          <p className="text-xs text-muted-foreground">
            Última evolução: {new Date(wound.latest_evolution.created_at).toLocaleDateString("pt-BR")}
            {" · "}
            {wound.latest_evolution.length_cm}cm × {wound.latest_evolution.width_cm}cm
          </p>
        </div>
      )}
    </Link>
  );
}

export default function PatientProfilePage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("lesoes");
  const [showWoundModal, setShowWoundModal] = useState(false);
  const [newWound, setNewWound] = useState({ wound_type: "ulcera_venosa", location: "", start_date: "", notes: "" });
  const [savingWound, setSavingWound] = useState(false);

  // Agendamentos
  const [appointments, setAppointments] = useState([]);
  const [showApptModal, setShowApptModal] = useState(false);
  const [apptForm, setApptForm] = useState({
    appointment_type: "retorno",
    scheduled_date: "",
    wound_id: "",
    notes: "",
  });
  const [savingAppt, setSavingAppt] = useState(false);
  const [apptMsg, setApptMsg] = useState("");

  // Medicações
  const [medications, setMedications] = useState([]);
  const [medLoading, setMedLoading] = useState(false);
  const [showMedForm, setShowMedForm] = useState(false);
  const [medForm, setMedForm] = useState({
    name: "", dose: "", frequency: "1x/dia", times: ["07:00"], notes: ""
  });
  const [savingMed, setSavingMed] = useState(false);
  const [medMsg, setMedMsg] = useState("");

  // Tendência glicêmica
  const [glucoseTrend, setGlucoseTrend] = useState(null);
  const [glucoseLoading, setGlucoseLoading] = useState(false);

  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const resp = await axios.get(`${API}/patients/${patientId}`, { withCredentials: true });
        setPatient(resp.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchPatient();
  }, [patientId]);

  useEffect(() => {
    if (activeTab === "consultas") {
      axios.get(`${API}/appointments?patient_id=${patientId}`, { withCredentials: true })
        .then(r => setAppointments(r.data || []))
        .catch(() => {});
    }
    if (activeTab === "medicacoes") {
      loadMedications();
    }
    if (activeTab === "glicemia" && !glucoseTrend && !glucoseLoading) {
      setGlucoseLoading(true);
      axios.get(`${API}/patients/${patientId}/glucose-trend`, { withCredentials: true })
        .then(r => setGlucoseTrend(r.data))
        .catch(() => setGlucoseTrend({ readings: [], alerts: [], stats: {} }))
        .finally(() => setGlucoseLoading(false));
    }
  }, [activeTab, patientId]);

  const loadMedications = () => {
    setMedLoading(true);
    axios.get(`${API}/patients/${patientId}/medications`, { withCredentials: true })
      .then(r => setMedications(r.data || []))
      .catch(() => {})
      .finally(() => setMedLoading(false));
  };

  const handleCreateWound = async (e) => {
    e.preventDefault();
    setSavingWound(true);
    try {
      const resp = await axios.post(`${API}/wounds`, {
        ...newWound,
        patient_id: patientId,
      }, { withCredentials: true });
      const updated = await axios.get(`${API}/patients/${patientId}`, { withCredentials: true });
      setPatient(updated.data);
      setShowWoundModal(false);
      setNewWound({ wound_type: "ulcera_venosa", location: "", start_date: "", notes: "" });
      navigate(`/wounds/${resp.data.wound_id}`);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingWound(false);
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!apptForm.scheduled_date) return;
    setSavingAppt(true);
    setApptMsg("");
    try {
      await axios.post(`${API}/appointments`, {
        ...apptForm,
        patient_id: patientId,
      }, { withCredentials: true });
      setApptMsg(patient?.phone
        ? "Consulta agendada! SMS enviado ao paciente."
        : "Consulta agendada! (paciente sem telefone cadastrado)");
      setShowApptModal(false);
      setApptForm({ appointment_type: "retorno", scheduled_date: "", wound_id: "", notes: "" });
      const r = await axios.get(`${API}/appointments?patient_id=${patientId}`, { withCredentials: true });
      setAppointments(r.data || []);
      setActiveTab("consultas");
    } catch (e) {
      setApptMsg("Erro ao agendar. Tente novamente.");
    } finally {
      setSavingAppt(false);
    }
  };

  const handleAddMedication = async (e) => {
    e.preventDefault();
    if (!medForm.name.trim() || !medForm.dose.trim()) return;
    setSavingMed(true);
    setMedMsg("");
    try {
      await axios.post(`${API}/patients/${patientId}/medications`, medForm, { withCredentials: true });
      setMedMsg("Medicação adicionada com sucesso!");
      setShowMedForm(false);
      setMedForm({ name: "", dose: "", frequency: "1x/dia", times: ["07:00"], notes: "" });
      loadMedications();
    } catch {
      setMedMsg("Erro ao adicionar medicação.");
    } finally {
      setSavingMed(false);
    }
  };

  const handleRemoveMedication = async (medId, medName) => {
    if (!window.confirm(`Remover "${medName}" da lista de medicações?`)) return;
    try {
      await axios.delete(`${API}/patients/${patientId}/medications/${medId}`, { withCredentials: true });
      loadMedications();
    } catch {
      alert("Erro ao remover medicação.");
    }
  };

  const addTimeField = () => setMedForm(p => ({ ...p, times: [...p.times, "07:00"] }));
  const updateTime = (i, v) => setMedForm(p => {
    const times = [...p.times]; times[i] = v; return { ...p, times };
  });
  const removeTime = (i) => setMedForm(p => ({
    ...p, times: p.times.filter((_, idx) => idx !== i)
  }));

  const handleCancelAppt = async (apptId) => {
    if (!window.confirm("Cancelar este agendamento?")) return;
    await axios.delete(`${API}/appointments/${apptId}`, { withCredentials: true });
    const r = await axios.get(`${API}/appointments?patient_id=${patientId}`, { withCredentials: true });
    setAppointments(r.data || []);
  };

  const handleMarkDone = async (apptId) => {
    await axios.put(`${API}/appointments/${apptId}/status`, { status: "realizado" }, { withCredentials: true });
    const r = await axios.get(`${API}/appointments?patient_id=${patientId}`, { withCredentials: true });
    setAppointments(r.data || []);
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4">
          <div className="h-32 rounded-xl bg-white border border-border animate-pulse" />
          <div className="h-48 rounded-xl bg-white border border-border animate-pulse" />
        </div>
      </Layout>
    );
  }

  if (!patient) {
    return <Layout><div className="text-center py-16 text-muted-foreground">Paciente não encontrado</div></Layout>;
  }

  const activeWounds = patient.wounds?.filter(w => w.status === "ativo") || [];
  const otherWounds = patient.wounds?.filter(w => w.status !== "ativo") || [];
  const isDiabetic = (patient.comorbidities || []).includes("Diabetes Mellitus");

  return (
    <Layout>
      <div data-testid="patient-profile-page" className="space-y-5">
        {/* Back + header */}
        <div className="flex items-center gap-3 animate-fade-in">
          <button
            onClick={() => navigate("/patients")}
            className="p-2 rounded-lg hover:bg-secondary transition-all"
            data-testid="back-to-patients"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>{patient.name}</h1>
        </div>

        {/* Patient info card */}
        <div className="bg-white rounded-xl border border-border p-5 animate-fade-in">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                {patient.name[0]}
              </div>
              <div>
                <h2 className="text-base font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>{patient.name}</h2>
                {patient.cpf && <p className="text-sm text-muted-foreground">CPF: {patient.cpf}</p>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {patient.birth_date && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-3.5 h-3.5" />
                <span>{patient.birth_date}</span>
              </div>
            )}
            {patient.phone && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="w-3.5 h-3.5" />
                <span>{patient.phone}</span>
              </div>
            )}
            {patient.city && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-3.5 h-3.5" />
                <span>{patient.city}</span>
              </div>
            )}
          </div>
          {patient.comorbidities?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-1.5">Comorbidades</p>
              <div className="flex flex-wrap gap-1.5">
                {patient.comorbidities.map(c => (
                  <span key={c} className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Equipe responsável */}
          {(patient.assigned_nurse_name || patient.assigned_acs_name || patient.assigned_doctor_name) && (
            <div className="mt-3 pt-3 border-t border-border/50">
              <div className="flex items-center gap-1.5 mb-2">
                <Users className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground">Equipe Responsável</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {patient.assigned_nurse_name && (
                  <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg" data-testid="assigned-nurse-display">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-3 h-3 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-muted-foreground">Enfermeiro(a)</p>
                      <p className="text-xs font-medium text-foreground truncate">{patient.assigned_nurse_name}</p>
                    </div>
                  </div>
                )}
                {patient.assigned_acs_name && (
                  <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-lg" data-testid="assigned-acs-display">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-3 h-3 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-muted-foreground">ACS</p>
                      <p className="text-xs font-medium text-foreground truncate">{patient.assigned_acs_name}</p>
                    </div>
                  </div>
                )}
                {patient.assigned_doctor_name && (
                  <div className="flex items-center gap-2 p-2 bg-primary/5 border border-primary/20 rounded-lg" data-testid="assigned-doctor-display">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-3 h-3 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-primary/70">Médico(a) — Alertas IA</p>
                      <p className="text-xs font-semibold text-primary truncate">{patient.assigned_doctor_name}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 animate-fade-in">
          <button
            onClick={() => setShowWoundModal(true)}
            className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all"
            data-testid="new-wound-button"
          >
            <Plus className="w-4 h-4" />
            Nova Lesão
          </button>
          <button
            onClick={() => setShowApptModal(true)}
            className="flex items-center justify-center gap-2 border border-primary/30 bg-primary/5 text-primary py-3 px-4 rounded-xl text-sm font-medium hover:bg-primary/10 transition-all"
            data-testid="schedule-appointment-btn"
          >
            <CalendarPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Agendar</span>
          </button>
          <button
            onClick={() => navigate(`/teleconsultation?patientId=${patientId}`)}
            className="flex items-center justify-center gap-2 border border-border bg-white py-3 px-4 rounded-xl text-sm font-medium hover:bg-secondary transition-all"
            data-testid="request-teleconsult-btn"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="hidden sm:inline">Interconsulta</span>
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-2 border border-border bg-white py-3 px-4 rounded-xl text-sm font-medium hover:bg-secondary transition-all"
            data-testid="export-pdf-btn"
            title="Exportar PDF"
          >
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">PDF</span>
          </button>
        </div>

        {/* Mensagem de confirmação do agendamento */}
        {apptMsg && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2 animate-fade-in">
            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">{apptMsg}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-border overflow-hidden animate-fade-in">
          <div className="flex border-b border-border overflow-x-auto">
            {[
              "lesoes",
              "medicacoes",
              ...(isDiabetic ? ["glicemia"] : []),
              "consultas",
              "historico"
            ].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 min-w-max py-3 px-3 text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab
                    ? "text-primary border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`tab-${tab}`}
              >
                {tab === "lesoes" ? "Lesões"
                  : tab === "medicacoes" ? "Medicações"
                  : tab === "glicemia" ? "Glicemia"
                  : tab === "consultas" ? "Consultas"
                  : "Histórico"}
              </button>
            ))}
          </div>
          <div className="p-4">
            {activeTab === "lesoes" && (
              <div className="space-y-3">
                {activeWounds.length === 0 && otherWounds.length === 0 ? (
                  <div className="text-center py-8">
                    <Activity className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma lesão cadastrada</p>
                    <button
                      onClick={() => setShowWoundModal(true)}
                      className="mt-2 text-sm text-primary font-medium hover:underline"
                    >
                      Cadastrar primeira lesão
                    </button>
                  </div>
                ) : (
                  <>
                    {activeWounds.map(w => (
                      <WoundCard key={w.wound_id} wound={w} />
                    ))}
                    {otherWounds.length > 0 && (
                      <>
                        <p className="text-xs text-muted-foreground font-medium pt-2">Histórico de lesões</p>
                        {otherWounds.map(w => (
                          <WoundCard key={w.wound_id} wound={w} />
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {activeTab === "consultas" && (
              <div className="space-y-3">
                {!patient?.phone && (
                  <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
                    <Phone className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                    <p className="text-xs text-yellow-800">Paciente sem telefone cadastrado — SMS não será enviado.</p>
                  </div>
                )}
                {appointments.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma consulta agendada</p>
                    <button
                      onClick={() => setShowApptModal(true)}
                      className="mt-2 text-sm text-primary font-medium hover:underline"
                      data-testid="schedule-first-appt-btn"
                    >
                      Agendar primeira consulta
                    </button>
                  </div>
                ) : (
                  appointments.map(appt => {
                    const dt = appt.scheduled_date ? new Date(appt.scheduled_date) : null;
                    const dateStr = dt ? dt.toLocaleDateString("pt-BR") : "—";
                    const timeStr = dt ? dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
                    const isPast = dt && dt < new Date();
                    return (
                      <div
                        key={appt.appointment_id}
                        className={`border rounded-xl p-4 ${appt.status === "cancelado" ? "opacity-50 bg-gray-50" : "bg-white"}`}
                        data-testid={`appt-card-${appt.appointment_id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${APPT_STATUS_COLORS[appt.status] || "bg-gray-100"}`}>
                                {appt.status === "agendado" ? "Agendada" : appt.status === "realizado" ? "Realizada" : "Cancelada"}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {APPT_TYPE_LABELS[appt.appointment_type] || appt.appointment_type}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                              <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              {dateStr}{timeStr ? ` — ${timeStr}` : ""}
                            </div>
                            {appt.notes && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{appt.notes}</p>}
                            <div className="flex items-center gap-3 mt-2">
                              {appt.sms_sent_immediate && (
                                <span className="text-xs text-green-600 flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />SMS enviado
                                </span>
                              )}
                              {!appt.sms_sent_immediate && patient?.phone && (
                                <span className="text-xs text-orange-500">SMS pendente</span>
                              )}
                            </div>
                          </div>
                          {appt.status === "agendado" && (
                            <div className="flex flex-col gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => handleMarkDone(appt.appointment_id)}
                                className="text-xs px-2.5 py-1.5 rounded-lg bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-all"
                                data-testid={`mark-done-${appt.appointment_id}`}
                              >
                                Realizada
                              </button>
                              <button
                                onClick={() => handleCancelAppt(appt.appointment_id)}
                                className="text-xs px-2.5 py-1.5 rounded-lg bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 transition-all"
                                data-testid={`cancel-appt-${appt.appointment_id}`}
                              >
                                Cancelar
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* ══ ABA GLICEMIA ══ */}
            {activeTab === "glicemia" && isDiabetic && (
              <div className="space-y-4" data-testid="tab-glicemia-content">
                {glucoseLoading ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="h-20 bg-secondary/50 rounded-xl animate-pulse" />)}
                  </div>
                ) : !glucoseTrend || glucoseTrend.readings.length === 0 ? (
                  <div className="text-center py-8">
                    <Activity className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma glicemia registrada ainda</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">Registre leituras nas evoluções da lesão para acompanhar a tendência</p>
                  </div>
                ) : (
                  <>
                    {/* Alertas proativos */}
                    {glucoseTrend.alerts.map((alert, i) => (
                      <div
                        key={i}
                        className={`flex items-start gap-3 rounded-xl p-3 border animate-fade-in ${
                          alert.level === "critico" ? "bg-red-50 border-red-200" :
                          alert.level === "alto" ? "bg-yellow-50 border-yellow-200" :
                          "bg-green-50 border-green-200"
                        }`}
                        data-testid={`glucose-alert-${alert.level}`}
                      >
                        {alert.level === "positivo"
                          ? <TrendingDown className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          : <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${alert.level === "critico" ? "text-red-600" : "text-yellow-600"}`} />
                        }
                        <p className={`text-xs font-medium ${
                          alert.level === "critico" ? "text-red-800" :
                          alert.level === "alto" ? "text-yellow-800" :
                          "text-green-800"
                        }`}>{alert.message}</p>
                      </div>
                    ))}

                    {/* Cards de estatísticas */}
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: "Média", value: `${glucoseTrend.stats.avg} mg/dL`, color: "text-foreground" },
                        { label: "Acima 180", value: glucoseTrend.stats.above_180, color: "text-yellow-700" },
                        { label: "Total leituras", value: glucoseTrend.stats.total_readings, color: "text-primary" },
                      ].map(s => (
                        <div key={s.label} className="bg-white border border-border rounded-xl p-3 text-center">
                          <p className={`text-xl font-bold ${s.color}`} style={{ fontFamily: "Manrope" }}>{s.value}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Gráfico de tendência */}
                    <div className="bg-white border border-border rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        <p className="text-sm font-semibold" style={{ fontFamily: "Manrope" }}>Tendência Glicêmica</p>
                        <span className="text-xs text-muted-foreground ml-auto">Últimas {Math.min(glucoseTrend.readings.length, 30)} leituras</span>
                      </div>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart
                          data={(() => {
                            // Agrupar por data — uma entrada por data com manhã/tarde/noite
                            const byDate = {};
                            (glucoseTrend.readings.slice(-30)).forEach(r => {
                              const key = r.date;
                              if (!byDate[key]) byDate[key] = { date: r.date };
                              byDate[key][r.period] = r.value_mgdl;
                            });
                            return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
                          })()}
                          margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={d => d.slice(5)} />
                          <YAxis tick={{ fontSize: 10 }} domain={[60, 'auto']} />
                          <Tooltip
                            formatter={(v, n) => [`${v} mg/dL`, n === "manha" ? "Manhã" : n === "tarde" ? "Tarde" : "Noite"]}
                            labelFormatter={l => `Data: ${l}`}
                          />
                          <ReferenceLine y={180} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "180", fontSize: 9, fill: "#f59e0b" }} />
                          <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="4 4" label={{ value: "70", fontSize: 9, fill: "#ef4444" }} />
                          <Legend formatter={v => v === "manha" ? "Manhã" : v === "tarde" ? "Tarde" : "Noite"} />
                          <Line type="monotone" dataKey="manha" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                          <Line type="monotone" dataKey="tarde" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                          <Line type="monotone" dataKey="noite" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                        </LineChart>
                      </ResponsiveContainer>
                      <p className="text-xs text-muted-foreground mt-2 text-center">
                        Linhas de referência: 70 mg/dL (hipoglicemia) · 180 mg/dL (hiperglicemia)
                      </p>
                    </div>

                    {/* Lista de leituras recentes */}
                    <div className="bg-white border border-border rounded-xl p-4">
                      <p className="text-sm font-semibold mb-3" style={{ fontFamily: "Manrope" }}>Leituras Recentes</p>
                      <div className="space-y-1.5 max-h-64 overflow-y-auto">
                        {[...glucoseTrend.readings].reverse().slice(0, 20).map((r, i) => {
                          const isCrit = r.value_mgdl > 300;
                          const isHigh = r.value_mgdl > 180;
                          const isLow = r.value_mgdl < 70;
                          const periodLabel = r.period === "manha" ? "Manhã" : r.period === "tarde" ? "Tarde" : "Noite";
                          return (
                            <div key={i} className={`flex items-center justify-between p-2 rounded-lg border ${isCrit ? "bg-red-50 border-red-200" : isHigh ? "bg-yellow-50 border-yellow-200" : isLow ? "bg-red-50 border-red-200" : "bg-secondary/30 border-transparent"}`}
                              data-testid={`glucose-list-item-${i}`}>
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-muted-foreground w-20">{r.date}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${r.period === "manha" ? "bg-blue-100 text-blue-700" : r.period === "tarde" ? "bg-orange-100 text-orange-700" : "bg-purple-100 text-purple-700"}`}>{periodLabel}</span>
                              </div>
                              <span className={`text-sm font-bold ${isCrit || isLow ? "text-red-700" : isHigh ? "text-yellow-700" : "text-green-700"}`}>
                                {r.value_mgdl} mg/dL
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === "historico" && (
              <div className="text-center py-8">
                <Camera className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Acesse uma lesão para ver o histórico de evoluções</p>
              </div>
            )}

            {/* ══ ABA MEDICAÇÕES ══ */}
            {activeTab === "medicacoes" && (
              <div className="space-y-3" data-testid="tab-medicacoes-content">
                {/* Aviso SMS */}
                <div className={`flex items-start gap-2 rounded-xl p-3 border ${patient?.phone ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`}>
                  <Bell className={`w-4 h-4 flex-shrink-0 mt-0.5 ${patient?.phone ? "text-green-600" : "text-yellow-600"}`} />
                  <p className={`text-xs ${patient?.phone ? "text-green-800" : "text-yellow-800"}`}>
                    {patient?.phone
                      ? `SMS de lembrete enviado diariamente às 8h para ${patient.phone}`
                      : "Paciente sem telefone cadastrado — o lembrete de SMS diário não será enviado. Edite o cadastro para adicionar o telefone."}
                  </p>
                </div>

                {/* Mensagem de feedback */}
                {medMsg && (
                  <div className={`flex items-center gap-2 rounded-xl p-3 border animate-fade-in ${medMsg.includes("Erro") ? "bg-red-50 border-red-200 text-red-800" : "bg-green-50 border-green-200 text-green-800"}`}>
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    <p className="text-xs">{medMsg}</p>
                  </div>
                )}

                {/* Botão adicionar */}
                <button
                  onClick={() => { setShowMedForm(true); setMedMsg(""); }}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-primary/30 rounded-xl text-sm font-medium text-primary hover:bg-primary/5 transition-all"
                  data-testid="add-medication-btn"
                >
                  <Plus className="w-4 h-4" /> Adicionar Medicação
                </button>

                {/* Formulário de adição */}
                {showMedForm && (
                  <div className="bg-secondary/40 border border-border rounded-xl p-4 animate-fade-in space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-foreground">Nova Medicação</p>
                      <button onClick={() => setShowMedForm(false)} className="p-1 rounded hover:bg-secondary">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <form onSubmit={handleAddMedication} className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome do Medicamento *</label>
                          <input
                            required
                            value={medForm.name}
                            onChange={e => setMedForm(p => ({ ...p, name: e.target.value }))}
                            placeholder="Ex: Metformina"
                            className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                            data-testid="med-name-input"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Dose *</label>
                          <input
                            required
                            value={medForm.dose}
                            onChange={e => setMedForm(p => ({ ...p, dose: e.target.value }))}
                            placeholder="Ex: 850mg"
                            className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                            data-testid="med-dose-input"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Frequência</label>
                        <select
                          value={medForm.frequency}
                          onChange={e => setMedForm(p => ({ ...p, frequency: e.target.value }))}
                          className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="med-frequency-select"
                        >
                          {["1x/dia","2x/dia","3x/dia","4x/dia","Conforme necessário","Outro"].map(f => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Horários</label>
                          <button type="button" onClick={addTimeField} className="text-xs text-primary hover:underline flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Horário
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {medForm.times.map((t, i) => (
                            <div key={i} className="flex items-center gap-1.5 bg-white border border-border rounded-lg px-2 py-1">
                              <input
                                type="time"
                                value={t}
                                onChange={e => updateTime(i, e.target.value)}
                                className="text-sm border-none outline-none bg-transparent w-24"
                                data-testid={`med-time-${i}`}
                              />
                              {medForm.times.length > 1 && (
                                <button type="button" onClick={() => removeTime(i)}>
                                  <X className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Observações</label>
                        <input
                          value={medForm.notes}
                          onChange={e => setMedForm(p => ({ ...p, notes: e.target.value }))}
                          placeholder="Ex: Tomar com alimento"
                          className="mt-1 w-full h-10 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="med-notes-input"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={savingMed}
                        className="w-full py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-all"
                        data-testid="save-medication-btn"
                      >
                        {savingMed ? "Salvando..." : "Salvar Medicação"}
                      </button>
                    </form>
                  </div>
                )}

                {/* Lista de medicações */}
                {medLoading ? (
                  <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-20 bg-secondary/50 rounded-xl animate-pulse" />)}</div>
                ) : medications.length === 0 ? (
                  <div className="text-center py-8">
                    <Pill className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma medicação cadastrada</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">Adicione medicamentos para habilitar o lembrete diário via SMS</p>
                  </div>
                ) : (
                  <>
                    {/* Ativas */}
                    {medications.filter(m => m.is_active).length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Medicações Ativas</p>
                        {medications.filter(m => m.is_active).map(med => (
                          <div key={med.medication_id} className="bg-white border border-border rounded-xl p-4" data-testid={`med-card-${med.medication_id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Pill className="w-4 h-4 text-primary" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-foreground">{med.name} <span className="font-normal text-muted-foreground">{med.dose}</span></p>
                                  <p className="text-xs text-muted-foreground mt-0.5">{med.frequency}</p>
                                  {med.times?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1.5">
                                      {med.times.map((t, i) => (
                                        <span key={i} className="flex items-center gap-1 text-xs bg-secondary px-2 py-0.5 rounded-full text-foreground">
                                          <Clock3 className="w-2.5 h-2.5" />{t}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {med.notes && <p className="text-xs text-muted-foreground/70 mt-1 italic">{med.notes}</p>}
                                  <p className="text-xs text-muted-foreground/50 mt-1.5">
                                    Adicionado por {med.added_by_name} em {new Date(med.added_at).toLocaleDateString("pt-BR")}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveMedication(med.medication_id, med.name)}
                                className="p-1.5 rounded-lg hover:bg-red-50 hover:text-destructive text-muted-foreground transition-all flex-shrink-0"
                                data-testid={`remove-med-${med.medication_id}`}
                                title="Remover medicação"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Histórico (inativas) */}
                    {medications.filter(m => !m.is_active).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer flex items-center gap-2 select-none">
                          <History className="w-3.5 h-3.5" /> Histórico de Medicações Removidas ({medications.filter(m => !m.is_active).length})
                        </summary>
                        <div className="space-y-2 mt-2">
                          {medications.filter(m => !m.is_active).map(med => (
                            <div key={med.medication_id} className="bg-gray-50 border border-border/50 rounded-xl p-3 opacity-60">
                              <p className="text-sm font-medium text-foreground line-through">{med.name} {med.dose}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{med.frequency}</p>
                              <div className="mt-1.5 space-y-0.5">
                                {(med.history || []).map((h, i) => (
                                  <p key={i} className="text-xs text-muted-foreground/60">
                                    {h.action === "adicionada" ? "Adicionada" : "Removida"} por {h.by_name} em {new Date(h.at).toLocaleDateString("pt-BR")} às {new Date(h.at).toLocaleTimeString("pt-BR", {hour:"2-digit",minute:"2-digit"})}
                                  </p>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal: Agendar Consulta */}
      {showApptModal && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowApptModal(false)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
                Agendar Consulta
              </h3>
              <button onClick={() => setShowApptModal(false)} className="p-1.5 rounded-lg hover:bg-secondary transition-all">
                <X className="w-4 h-4" />
              </button>
            </div>

            {!patient?.phone && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                <Phone className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800">
                  Paciente sem telefone cadastrado. O agendamento será salvo, mas <strong>nenhum SMS será enviado</strong>.
                  Adicione o telefone no cadastro do paciente.
                </p>
              </div>
            )}

            <form onSubmit={handleCreateAppointment} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tipo de Consulta</label>
                <select
                  value={apptForm.appointment_type}
                  onChange={e => setApptForm(p => ({ ...p, appointment_type: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="appt-type-select"
                >
                  <option value="retorno">Consulta de Retorno</option>
                  <option value="curativo">Curativo / Troca de Curativo</option>
                  <option value="avaliacao">Avaliação Clínica</option>
                  <option value="interconsulta">Interconsulta</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Data e Hora</label>
                <input
                  type="datetime-local"
                  required
                  value={apptForm.scheduled_date}
                  onChange={e => setApptForm(p => ({ ...p, scheduled_date: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="appt-date-input"
                />
              </div>

              {patient?.wounds?.filter(w => w.status === "ativo").length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Lesão (opcional)</label>
                  <select
                    value={apptForm.wound_id}
                    onChange={e => setApptForm(p => ({ ...p, wound_id: e.target.value }))}
                    className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    data-testid="appt-wound-select"
                  >
                    <option value="">— Consulta geral —</option>
                    {patient.wounds.filter(w => w.status === "ativo").map(w => (
                      <option key={w.wound_id} value={w.wound_id}>
                        {WOUND_LABELS[w.wound_type] || w.wound_type}{w.location ? ` — ${w.location}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Observações</label>
                <textarea
                  value={apptForm.notes}
                  onChange={e => setApptForm(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  placeholder="Ex: Trazer exames, jejum necessário..."
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  data-testid="appt-notes-input"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowApptModal(false)}
                  className="flex-1 py-3 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingAppt || !apptForm.scheduled_date}
                  className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                  data-testid="save-appt-button"
                >
                  <CalendarPlus className="w-4 h-4" />
                  {savingAppt ? "Agendando..." : "Agendar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New wound modal */}
      {showWoundModal && (
        <div
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowWoundModal(false)}
        >
          <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-fade-in">
            <h3 className="text-lg font-bold text-foreground mb-4" style={{ fontFamily: 'Manrope' }}>
              Nova Lesão
            </h3>
            <form onSubmit={handleCreateWound} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tipo de Lesão</label>
                <select
                  value={newWound.wound_type}
                  onChange={e => setNewWound(p => ({ ...p, wound_type: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="wound-type-select"
                >
                  <option value="ulcera_venosa">Úlcera Venosa</option>
                  <option value="pe_diabetico">Pé Diabético</option>
                  <option value="lesao_por_pressao">Lesão por Pressão</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Localização</label>
                <input
                  type="text"
                  placeholder="Ex: maléolo medial esquerdo"
                  value={newWound.location}
                  onChange={e => setNewWound(p => ({ ...p, location: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="wound-location-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Data de Início</label>
                <input
                  type="date"
                  value={newWound.start_date}
                  onChange={e => setNewWound(p => ({ ...p, start_date: e.target.value }))}
                  className="mt-1 w-full h-12 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="wound-start-date-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowWoundModal(false)}
                  className="flex-1 py-3 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingWound}
                  className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
                  data-testid="save-wound-button"
                >
                  {savingWound ? "Salvando..." : "Criar Lesão"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
